import React, { useEffect, useRef, useState } from "react";

const formatClock = (ms) => {
  const totalSec = Math.max(0, Math.round(Number(ms) / 1000));
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  const pad = (value) => String(value).padStart(2, "0");
  if (hours > 0) return `${hours}:${pad(minutes)}:${pad(seconds)}`;
  return `${minutes}:${pad(seconds)}`;
};

const timeText = (progress, now) => {
  const startedAt = progress?.startedAt;
  if (!startedAt) return "";
  if (progress.status === "done" || progress.status === "failed") {
    const end = progress.updatedAt || now;
    return formatClock(Math.max(0, end - startedAt));
  }
  return formatClock(Math.max(0, now - startedAt));
};

const STREAMS = {
  product: () =>
    `${process.env.REACT_APP_API_URL || ""}api/v1/App/productimport/progress`,
  customer: () =>
    `${process.env.REACT_APP_API_URL || ""}api/v1/App/customer/import-progress`,
};

const parseEvents = (buffer, onEvent) => {
  const parts = buffer.split("\n\n");
  const rest = parts.pop() || "";
  parts.forEach((block) => {
    const data = block
      .split("\n")
      .filter((line) => line.startsWith("data:"))
      .map((line) => line.slice(5).trim())
      .join("");
    if (!data) return;
    try {
      onEvent(JSON.parse(data));
    } catch (err) {
      console.error(err);
    }
  });
  return rest;
};

const isActiveImport = (payload) =>
  payload &&
  payload.status &&
  payload.status !== "idle" &&
  Number(payload.total) > 0;

const ProductImportStatusBar = () => {
  const [progress, setProgress] = useState(null);
  const [now, setNow] = useState(Date.now());
  const activeRef = useRef({ product: false, customer: false });
  const controllersRef = useRef({ product: null, customer: null });
  /** True after gis-*-import event (or resume) until done/failed — ignore early SSE idle. */
  const expectingRef = useRef({ product: false, customer: false });
  const hideTimerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    if (!progress || progress.status === "idle") return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [progress?.status, progress?.startedAt, progress?.phaseStartedAt]);

  useEffect(() => {
    mountedRef.current = true;

    const stopStream = (kind) => {
      activeRef.current[kind] = false;
      const controller = controllersRef.current[kind];
      if (controller) {
        try {
          controller.abort();
        } catch (_) {
          /* ignore */
        }
        controllersRef.current[kind] = null;
      }
    };

    const apply = (payload) => {
      if (!mountedRef.current) return;
      if (!isActiveImport(payload)) {
        // Upload returns before first publish; SSE often sends idle first.
        // Do not wipe the optimistic chip or close the stream while expecting.
        if (expectingRef.current.product || expectingRef.current.customer) {
          return;
        }
        setProgress(null);
        stopStream("product");
        stopStream("customer");
        return;
      }
      setProgress((prev) => {
        const phaseChanged =
          !prev ||
          prev.label !== payload.label ||
          (payload.done || 0) < (prev.done || 0);
        const startedAt =
          payload.startedAt ||
          (phaseChanged ? Date.now() : prev?.startedAt) ||
          Date.now();
        return {
          ...payload,
          startedAt,
          phaseStartedAt:
            payload.phaseStartedAt ||
            (phaseChanged ? Date.now() : prev?.phaseStartedAt) ||
            startedAt,
        };
      });
      setNow(Date.now());
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      if (payload.status === "done" || payload.status === "failed") {
        expectingRef.current = { product: false, customer: false };
        hideTimerRef.current = setTimeout(() => {
          if (!mountedRef.current) return;
          setProgress(null);
          stopStream("product");
          stopStream("customer");
        }, 8000);
      }
    };

    const connectOne = async (kind) => {
      if (activeRef.current[kind]) return;
      activeRef.current[kind] = true;
      let backoffMs = 2000;
      let controller = null;

      while (mountedRef.current && activeRef.current[kind]) {
        const token = localStorage.getItem("UserToken");
        if (!token) {
          await new Promise((r) => setTimeout(r, 2000));
          continue;
        }

        controller = new AbortController();
        controllersRef.current[kind] = controller;

        try {
          const response = await fetch(STREAMS[kind](), {
            method: "GET",
            headers: {
              Authorization: token,
              Accept: "text/event-stream",
            },
            signal: controller.signal,
          });
          const type = response.headers.get("content-type") || "";
          if (!response.ok || type.includes("application/json")) {
            // Auth/proxy JSON — back off; do not hammer the API
            await new Promise((r) => setTimeout(r, Math.min(backoffMs, 30000)));
            backoffMs = Math.min(backoffMs * 2, 30000);
            continue;
          }

          backoffMs = 2000;
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          while (mountedRef.current && activeRef.current[kind]) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer = parseEvents(
              buffer + decoder.decode(value, { stream: true }),
              apply,
            );
          }
        } catch (err) {
          if (err?.name === "AbortError") break;
        }

        if (!mountedRef.current || !activeRef.current[kind]) break;
        // Stream dropped while import still needed — soft reconnect
        await new Promise((r) => setTimeout(r, backoffMs));
        backoffMs = Math.min(backoffMs * 2, 15000);
      }

      if (controllersRef.current[kind] === controller) {
        controllersRef.current[kind] = null;
      }
      activeRef.current[kind] = false;
    };

    const startForEvent = (kind, event) => {
      expectingRef.current[kind] = true;
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      if (event?.detail) apply(event.detail);
      connectOne(kind);
    };

    const onProductStarted = (event) => startForEvent("product", event);
    const onCustomerStarted = (event) => startForEvent("customer", event);

    window.addEventListener("gis-product-import", onProductStarted);
    window.addEventListener("gis-customer-import", onCustomerStarted);

    // One short check on mount (resume bar after refresh mid-import), then stop if idle
    const resumeCheck = async () => {
      const token = localStorage.getItem("UserToken");
      if (!token) return;
      for (const kind of ["product", "customer"]) {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 8000);
        try {
          const response = await fetch(STREAMS[kind](), {
            method: "GET",
            headers: {
              Authorization: token,
              Accept: "text/event-stream",
            },
            signal: controller.signal,
          });
          const type = response.headers.get("content-type") || "";
          if (!response.ok || type.includes("application/json")) continue;
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          let sawActive = false;
          const deadline = Date.now() + 2500;
          while (Date.now() < deadline) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer = parseEvents(
              buffer + decoder.decode(value, { stream: true }),
              (payload) => {
                if (isActiveImport(payload)) {
                  sawActive = true;
                  apply(payload);
                }
              },
            );
            if (sawActive) break;
          }
          controller.abort();
          if (sawActive) {
            expectingRef.current[kind] = true;
            connectOne(kind);
          }
        } catch (_) {
          /* ignore resume failures */
        } finally {
          clearTimeout(timeout);
        }
      }
    };
    resumeCheck();

    return () => {
      mountedRef.current = false;
      stopStream("product");
      stopStream("customer");
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      window.removeEventListener("gis-product-import", onProductStarted);
      window.removeEventListener("gis-customer-import", onCustomerStarted);
    };
  }, []);

  const show = progress && progress.total > 0 && progress.status !== "idle";
  if (!show) return null;

  const stopped = progress.status === "failed";

  return (
    <div
      className={`product-import-status-chip${stopped ? " is-stopped" : ""}`}
      role="status"
    >
      <style>{`
        .product-import-status-chip {
          position: fixed;
          top: 24px;
          left: calc(50% + 52px);
          transform: translateX(-50%);
          z-index: 30;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          height: 32px;
          padding: 0 12px;
          border: 1px solid #5CC7C1;
          border-radius: 16px;
          background: #f3fbfb;
          color: #1f8f89;
          font-size: 13px;
          font-weight: 600;
          white-space: nowrap;
        }
        .product-import-status-time {
          font-size: 12px;
          font-weight: 500;
        }
        @media (max-width: 1200px) {
          .product-import-status-chip {
            top: 14px;
            left: calc(50% + 42px);
          }
        }
        .product-import-status-chip.is-stopped {
          border-color: #e7b3b3;
          background: #fff6f6;
          color: #c44545;
        }
      `}</style>
      <span>{progress.label || "Product import"}</span>
      <span>
        {progress.status === "done" && progress.stats
          ? `${
              (progress.stats.created || 0) +
              (progress.stats.updated || 0) +
              (progress.stats.restored || 0)
            } saved (${progress.stats.created || 0} new)`
          : `${progress.done || 0}/${progress.total}`}
      </span>
      {timeText(progress, now) ? (
        <span className="product-import-status-time">
          · {timeText(progress, now)}
        </span>
      ) : null}
    </div>
  );
};

export default ProductImportStatusBar;
